export type Result<T> = { ok: true; value: T } | { ok: false; error: unknown }
export type VoidResult = { ok: true } | { ok: false; error: unknown }

export const okResult = <T>(value: T): Result<T> => ({ ok: true, value })
export const errorResult = <T>(error?: unknown): Result<T> => ({ ok: false, error })
export const voidResult = (): VoidResult => ({ ok: true })

// This is a crazy cool utility type that converts a camel case string to snake case.
// The basic premise is that it walks the string one character at a time, building up
// the result string as it goes, and if it encounters an uppercase character it prepends
// it with an underscore in the result string.
//
// There are two exceptions to handle:
//
// First, if it encounters multiple uppercase letters in a row, as in allowBNPL, we don't
// want underscores between each of the uppercase letters. So we include an additional
// boolean type argument that represents if the last letter was uppercase. If so, the
// new character is appended without a leading underscore.
//
// The second thing is an exception to the first thing. We want the string allowBNPLater to
// result in allow_bnp_later, meaning that the last uppercase letter actually *should* have an
// underscore before it even though it comes after an uppercase letter. So we have one additional
// check for this by doing a lookahead on the rest of the string to see if it starts with a
// lower case letter.
//
// Phew.
type CamelToSnakeCase<S extends string, wasLastUppercase = false> = S extends `${infer T}${infer U}`
    ? T extends Lowercase<T>
        ? `${T}${CamelToSnakeCase<U, false>}`
        : wasLastUppercase extends true
          ? U extends `${infer FirstU}${infer _RestU}`
              ? FirstU extends Lowercase<FirstU>
                  ? `_${Lowercase<T>}${CamelToSnakeCase<U, true>}`
                  : `${Lowercase<T>}${CamelToSnakeCase<U, true>}`
              : `${Lowercase<T>}${CamelToSnakeCase<U, true>}`
          : `_${Lowercase<T>}${CamelToSnakeCase<U, true>}`
    : S

export type SnakeCaseKeys<T> = { [K in keyof T as CamelToSnakeCase<string & K>]: T[K] }
